import { useState } from 'react';
import { useRouter } from 'next/router';

import {
  Box,
  Button,
  ButtonGroup,
  Heading,
  Label,
  LabeledControl,
  Modal,
  Paragraph,
  Switch,
  Toast,
  useToast,
} from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyFeedOverrideWebApiHandler_getFeedOverrideSections,
  useRentalManagerUserPropertyFeedOverrideWebApiHandler_setFeedOverrideSection,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useReimportLock } from '../../hooks/useReimportLock';
import accessUtils from '../../utils/accessUtils';
import { FEED_OVERRIDES_TAG } from '../../constants/FeedOverrideConstants';

import type { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';

type FeedOverridesSwitchProps = {
  section: FeedOverrideSection;
};

const FeedOverridesSwitch = ({ section }: FeedOverridesSwitchProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { enqueueToast } = useToast();
  const { isReimportLocked, startReimportLock } = useReimportLock({ listingAlias });

  const [modalOpen, setModalOpen] = useState(false);

  const { data } = useRentalManagerUserPropertyFeedOverrideWebApiHandler_getFeedOverrideSections(
    { listingAlias },
    {
      enabled: Boolean(listingAlias) && accessUtils.canAccessFeedOverrides(),
      meta: {
        tags: [FEED_OVERRIDES_TAG],
        context: { api: 'rental-manager-api', details: 'FeedOverridesSwitch' },
      },
    },
  );

  const { mutate: setFeedOverride, isPending } =
    useRentalManagerUserPropertyFeedOverrideWebApiHandler_setFeedOverrideSection({
      meta: {
        invalidates: [FEED_OVERRIDES_TAG, 'listingDetails'],
        context: { api: 'rental-manager-api', details: 'FeedOverridesSwitch' },
      },
    });

  if (!accessUtils.canAccessFeedOverrides()) {
    return null;
  }

  const feedOverridesEnabled = data?.sections.find((s) => s.section === section)?.enabled ?? false;

  const handleConfirm = () => {
    setFeedOverride(
      { postData: { listingAlias, section, enabled: !feedOverridesEnabled } },
      {
        onSuccess: () => {
          if (feedOverridesEnabled) {
            startReimportLock();
          }
          enqueueToast(
            <Toast id="feed-overrides-success" tone="success">
              {feedOverridesEnabled ? 'Feed overrides turned off' : 'Feed overrides turned on'}
            </Toast>,
          );
          setModalOpen(false);
        },
        onError: () => {
          enqueueToast(
            <Toast tone="critical">
              {feedOverridesEnabled
                ? 'Could not turn off feed overrides. Try again.'
                : 'Could not enable feed overrides. Try again.'}
            </Toast>,
          );
          setModalOpen(false);
        },
      },
    );
  };

  const handleCancel = () => {
    setModalOpen(false);
  };

  return (
    <Box css={{ marginBottom: '600', paddingBottom: '400' }}>
      <LabeledControl
        label={<Label>Enable feed overrides</Label>}
        labelPosition="left"
        control={
          <Switch
            checked={feedOverridesEnabled}
            onChange={() => setModalOpen(true)}
            disabled={isPending || isReimportLocked}
          />
        }
        css={{ marginBottom: '0' }}
      />
      <Modal
        open={modalOpen}
        header={
          <Heading level={2}>{feedOverridesEnabled ? 'Turn off feed overrides' : 'Allow feed overrides'}</Heading>
        }
        body={
          <Paragraph>
            {feedOverridesEnabled
              ? 'This will reset all fields to match the feed and remove any manual changes.'
              : 'When overrides are allowed, changes you make here will not sync with the feed. Turn this off to restore feed syncing.'}
          </Paragraph>
        }
        footer={
          <ButtonGroup aria-label="Feed overrides confirmation">
            <Button tone="brand" emphasis="outlined" onClick={handleCancel} disabled={isPending}>
              Cancel
            </Button>
            <Button tone="brand" emphasis="filled" onClick={handleConfirm} loading={isPending}>
              {feedOverridesEnabled ? 'Turn off overrides' : 'Allow overrides'}
            </Button>
          </ButtonGroup>
        }
        onOpenChange={(open) => !open && !isPending && handleCancel()}
        size="xs"
        dividers
      />
    </Box>
  );
};

export default FeedOverridesSwitch;
